import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Clock, Instagram, Facebook, ShieldCheck } from "lucide-react";
import { BUSINESS, SERVICES, SERVICE_AREAS } from "../data/business";

export default function Footer() {
  return (
    <footer className="bg-[#1A1D21] text-white pt-12 pb-24 xl:pb-8" data-testid="site-footer">
      <div className="abc-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="footer-logo-link">
            <img
              src="/drain-sights-logo.png"
              alt="Drain Sights logo"
              className="w-10 h-10 rounded-xl object-cover group-hover:scale-110 transition-transform duration-200"
            />
            <span className="font-display text-lg font-extrabold leading-tight">Drain <span className="text-[#E31D2B]">Sights</span></span>
          </Link>
          <p className="mt-4 text-white/70 max-w-sm leading-relaxed">
            Drain &amp; sewer specialists serving the Baltimore and Temple Hills / DC metros. Drain cleaning, camera inspection, hydro-jetting, descaling, and sewer line work — with upfront pricing and clean workmanship.
          </p>
          <div className="mt-5 space-y-2 text-sm text-white/80">
            <a href={BUSINESS.phoneHref} className="flex items-center gap-2 hover:text-white" data-testid="footer-phone"><Phone className="w-4 h-4 text-[#E31D2B]" /> {BUSINESS.phone}</a>
            <a href={BUSINESS.emailHref} className="flex items-center gap-2 hover:text-white break-all" data-testid="footer-email"><Mail className="w-4 h-4 text-[#E31D2B] flex-shrink-0" /> {BUSINESS.email}</a>
            {/* Service-area business — no storefront address shown site-wide (mailing address lives on the Contact page only). */}
            {/*
              Hours — visible text + machine-readable schema.org microdata.
              The JSON-LD `OpeningHoursSpecification` in /public/index.html is
              the primary SEO signal; this `<meta itemprop="openingHours">`
              tag is a secondary belt-and-braces hint for crawlers that read
              inline microdata. The user-facing copy (24/7 Emergency
              Service, open 24 hours / 7 days a week) is the natural-language version Google's
              snippet generator picks up.
            */}
            <div
              className="flex items-start gap-2"
              itemScope
              itemType="https://schema.org/LocalBusiness"
              data-testid="footer-hours"
            >
              <Clock className="w-4 h-4 text-[#E31D2B] mt-0.5 flex-shrink-0" />
              <span>
                <span className="font-semibold text-white">{BUSINESS.hours.display}</span>
                <br />
                <span>{BUSINESS.hours.detail}</span>
                <meta itemProp="openingHours" content={BUSINESS.hours.schema} />
              </span>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <a href={BUSINESS.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 inline-flex items-center justify-center transition" data-testid="footer-instagram">
              <Instagram className="w-4 h-4" />
            </a>
            <a href={BUSINESS.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 inline-flex items-center justify-center transition" data-testid="footer-facebook">
              <Facebook className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-display font-bold mb-4 text-sm uppercase tracking-widest text-white/90">Services</h4>
          <ul className="space-y-2 text-sm text-white/70">
            {SERVICES.slice(0, 10).map((s) => (
              <li key={s.slug}><Link to={`/services/${s.slug}`} className="hover:text-white" data-testid={`footer-service-${s.slug}`}>{s.name}</Link></li>
            ))}
            <li><Link to="/services" className="text-[#E31D2B] hover:text-white font-semibold" data-testid="footer-all-services">View all services →</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold mb-4 text-sm uppercase tracking-widest text-white/90">Service Areas</h4>
          <ul className="space-y-2 text-sm text-white/70">
            {SERVICE_AREAS.map((a) => (
              <li key={a.slug}><Link to={`/service-areas/${a.slug}`} className="hover:text-white" data-testid={`footer-area-${a.slug}`}>Service in {a.city}, {a.state}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold mb-4 text-sm uppercase tracking-widest text-white/90">Company</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/about" className="hover:text-white" data-testid="footer-about">About</Link></li>
            <li><Link to="/reviews" className="hover:text-white" data-testid="footer-reviews">Reviews</Link></li>
            <li><Link to="/faq" className="hover:text-white" data-testid="footer-faq">FAQs</Link></li>
            <li><Link to="/contact" className="hover:text-white" data-testid="footer-contact">Contact</Link></li>
            <li><Link to="/gallery" className="hover:text-white" data-testid="footer-gallery">Gallery</Link></li>
            <li><a href="https://legal.getdrainsights.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</a></li>
            <li><a href="https://legal.getdrainsights.com/terms" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="footer-terms">Terms of Service</a></li>
            <li><a href="https://legal.getdrainsights.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="footer-cookies">Cookie Preferences</a></li>
          </ul>
        </div>
      </div>

      <div className="abc-container mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/60">
        <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#E31D2B]" /> {BUSINESS.license}</div>
        <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-4">
          <span>© {new Date().getFullYear()} DRAIN SIGHTS, L.L.C. All rights reserved.</span>
          <a
            href="https://reviewpresence.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/60 hover:text-white transition-colors"
            data-testid="footer-agency-credit"
          >
            Website by Review Presence
          </a>
        </div>
      </div>
    </footer>
  );
}
